import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { useApi } from "../../context/ApiContext";
import Show from "components/Show";

interface Cart {
 _id: string;
 user?: {
  _id: string;
  name: string;
  email: string;
 };
 email: string;
 total: number;
 createdAt: string;
 items: {
  product: {
   _id: string;
   name: string;
  };
  quantity: number;
  price: number;
 }[];
}

export const Carts: React.FC = () => {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const { api } = useApi();
 const [carts, setCarts] = useState<Cart[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [search, setSearch] = useState("");
 const [page, setPage] = useState(1);
 const [total, setTotal] = useState(0);
 const [limit] = useState(20);
 const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
 const [bulkActionLoading] = useState(false);
 const searchInputRef = useRef<HTMLInputElement>(null);
 const wasSearchFocusedRef = useRef(false);


 useEffect(() => {
  fetchCarts();
 }, [page, search]);

 // Maintain focus on search input
 useEffect(() => {
  if (wasSearchFocusedRef.current && searchInputRef.current && document.activeElement !== searchInputRef.current) {
   const cursorPosition = searchInputRef.current.selectionStart || search.length;
   searchInputRef.current.focus();
   searchInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
  }
 }, [search]);

 const fetchCarts = async () => {
  try {
   setLoading(true);
   setError(null);
   const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...(search && { search: search }),
   });
   
   const response = await api.get(`/cart/admin?${params.toString()}`);
   setCarts(response.data.data || []);
   setTotal(response.data.pagination?.total || 0);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.carts.errors.fetch"));
  } finally {
   setLoading(false);
  }
 };



 const handleSelectAll = (checked: boolean) => {
  if (checked) {
   setSelectedIds(new Set(carts.map((c) => c._id)));
  } else {
   setSelectedIds(new Set());
  }
 };

 const handleSelectOne = (id: string, checked: boolean) => {
  const newSelected = new Set(selectedIds);
  if (checked) {
   newSelected.add(id);
  } else {
   newSelected.delete(id);
  }
  setSelectedIds(newSelected);
 };


 const handleBulkAction = async (action: string) => {
  if (selectedIds.size === 0) return;

  if (action === "") return; // Placeholder selected, do nothing

  if (action === "clear") {
   setSelectedIds(new Set());
   return;
  }

 };
 const totalPages = Math.ceil(total / limit);

 if (loading && carts.length === 0) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <Heading size="xl">{t("admin.carts.title")}</Heading>

    {error && <Alert variant="error">{error}</Alert>}

    <Card padding="lg">
     <Stack spacing="md">
      {selectedIds.size > 0 && (
       <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 ">
        <div className="text-sm font-medium text-blue-900">
         {t("admin.carts.selectedCount", { count: selectedIds.size })}
        </div>
        <div className="flex gap-2 items-center">
         <select
          onChange={(e) => {
           const value = e.target.value;
           handleBulkAction(value);
           // Reset select to placeholder after action
           e.target.value = "";
          }}
          disabled={bulkActionLoading}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-white min-w-[180px]"
          defaultValue=""
         >
          <option value="" disabled>{t("admin.carts.selectBulkAction") || "Select action..."}</option>
          <option value="clear">{t("admin.carts.clearSelection")}</option>
         </select>
         {(bulkActionLoading) && (
          <Spinner size="sm" />
         )}
        </div>
       </div>
      )}
      <Show ifcond={false}>
        
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
       <Input
        ref={searchInputRef}
        placeholder={t("admin.carts.searchPlaceholder")}
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
        onFocus={() => { wasSearchFocusedRef.current = true; }}
        onBlur={() => {
          setTimeout(() => {
            if (document.activeElement !== searchInputRef.current) {
              wasSearchFocusedRef.current = false;
            }
          }, 0);
        }}
        />
      </div>

        </Show>
      <div className="overflow-x-auto">
       <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
         <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           <input
            type="checkbox"
            checked={selectedIds.size > 0 && selectedIds.size === carts.length}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="rounded border-gray-300"
           />
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.carts.customer")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.carts.date")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           {t("admin.carts.itemsCount")}
          </th>
         </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
         {carts.length === 0 ? (
          <tr>
           <td colSpan={10} className="px-6 py-8 text-center text-gray-500">
            {t("admin.carts.noCarts")}
           </td>
          </tr>
         ) : (
         carts.map((cart) => (
          <tr key={cart._id}>
           <td className="px-6 py-4 whitespace-nowrap">
            <input
             type="checkbox"
             checked={selectedIds.has(cart._id)}
             onChange={(e) => handleSelectOne(cart._id, e.target.checked)}
             className="rounded border-gray-300"
            />
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
            <button
              onClick={() => navigate(`/admin/carts/${cart._id}`)}
             className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
            >
                {cart.user?.name && cart.user?.email ? `${cart.user?.name} <${cart.user?.email}>` : cart.user?.email || cart.user?.name || t("admin.carts.unknown")}
            </button>
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {new Date(cart.createdAt).toLocaleDateString()}
           </td>
           <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {cart.items.length}
           </td>
          </tr>
         )))}
        </tbody>
       </table>
      </div>

      {totalPages > 1 && (
       <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
         {t("admin.orders.showing", { start: ((page - 1) * limit) + 1, end: Math.min(page * limit, total), total })}
        </div>
        <div className="flex gap-2">
         <Button
          variant="outline"
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
         >
          {t("common.previous")}
         </Button>
         <div className="flex items-center gap-2">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
           const pageNum = i + 1;
           return (
            <Button
             key={pageNum}
             variant={page === pageNum ? "primary" : "outline"}
             size="sm"
             onClick={() => setPage(pageNum)}
            >
             {pageNum}
            </Button>
           );
          })}
         </div>
         <Button
          variant="outline"
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
         >
          {t("common.next")}
         </Button>
        </div>
       </div>
      )}
     </Stack>
    </Card>
   </Stack>
  </Container>
 );
};

export default Carts;
